import { EMode, TSeoKeywordData } from '@microfrontends/app-shell-v2'
import { Col, ELanguage, HogProvider, Row, useIdentify } from '@microfrontends/react-components'
import { useCallback, useEffect, useState } from 'react'
import { getParamFromUrl } from '../../helpers/url'
import { IAuthUser } from '../../services/authService'
import { getCompanyList, ICompanyListResponse } from '../../services/companyListService'
import { TAuthUser } from '../../services/userService'
import { useStore } from '../../store'
import { ACTION_SET_COMPANY_LIST, ACTION_SET_USER } from '../../store/actions'
import CompanyListSection from '../CompanyListSection'
import CompanySearchHeader from '../CompanySearchHeader'
import CompanySearchResult from '../CompanySearchResult'
import { LIMIT_COMPANY_LIST, UPDATE_USER } from '../constants'
import GotoTop from '../GotoTop'
import MasterLayout from '../MasterLayout'
import translations from './translation'

const LIMIT_SEARCH_RESULT = 10
const msBaseUrl = process.env.NEXT_PUBLIC_MICROSERVICE_DOMAIN ?? ''
const isOptOutUserAgentFilter = process.env.NEXT_PUBLIC_POSTHOG_OPT_OUT_USERAGENT_FILTER === '1'
interface ICompanyListProps {
    jsToken: string
    wsToken: string
    langCode: ELanguage
    user: IAuthUser
    appMode: EMode
    keywordFromUrl: string
    industriesFromUrl: number[]
    companyList: ICompanyListResponse[]
    seoKeywords: TSeoKeywordData[]
}

interface IDoSearchParam {
    searchKeyword?: string
    searchIndustries?: number[]
    page?: number
}

const CompanyListPage = (props: ICompanyListProps) => {
    const { appMode, langCode, jsToken, wsToken, user, keywordFromUrl = '', industriesFromUrl = [], seoKeywords } = props
    const companyListProps = props.companyList

    const { state, dispatch } = useStore()
    useIdentify(user, msBaseUrl)
    
    // Search Section
    const [searchResult, setSearchResult] = useState<ICompanyListResponse[]>([])
    const [searchPage, setSearchPage] = useState<number>(0)
    const [isHaveMore, setHaveMore] = useState<boolean>(false)
    // End search section
    const [keyword, setKeyword] = useState<string>(keywordFromUrl)
    const [selectedIndustry, setSelectedIndustry] = useState<number[]>(industriesFromUrl)
    // is search or default page
    const [isSearch, setSearch] = useState<boolean>(false)
    // currently calling API
    const [isSearching, setSearching] = useState<boolean>(false)

    const { isMobile } = state

    const t = useCallback(
        (text: string): string => {
            if (langCode === ELanguage.EN) {
                return text
            } else {
                return translations[text]
            }
        },
        [langCode]
    )

    const updateHistory = (searchKeyword: string, searchCategories: number[]) => {
        // bring keyword to url by history.pushState
        if (typeof window !== 'undefined') {
            const { origin, pathname } = window.location
            let url = `${origin}${pathname}`
            if (searchKeyword !== '') {
                url = `${url}?keyword=${searchKeyword}`
            }
            if (searchCategories && searchCategories.length > 0) {
                if (searchKeyword !== '') {
                    url = `${url}&`
                } else {
                    url = `${url}?`
                }
                url = `${url}category=${searchCategories.join(',')}`
            }
            history.pushState(null, '', url)
        }
    }

    const doSearch = useCallback(
        async (param: IDoSearchParam) => {
            const { searchKeyword = '', searchIndustries = [], page = null } = param
            setSearching(true)
            let filterPage = page !== null ? page : searchPage
            if (searchKeyword !== '') {
                // It is branch new search, so we need to reset page index to zero
                filterPage = 0
            }
            let industries = searchIndustries
            if (searchIndustries.length === 0) {
                industries = selectedIndustry
            }
            const userId = user && user?.userId ? user?.userId : 0
            const filters = []
            if (industries.length > 0) {
                filters.push({
                    field: 'industries.industryId',
                    value: industries.join(',')
                })
            }
            const searchFilter = {
                query: searchKeyword !== '' ? searchKeyword : keyword,
                order: [],
                facets: [],
                hitsPerPage: LIMIT_SEARCH_RESULT,
                filter: filters,
                page: filterPage,
                userId: userId,
                isJobs: true
            }

            const accessToken = user?.accessToken ? user?.accessToken : ''
            const companyListResult = await getCompanyList(searchFilter, langCode, accessToken)

            if (!companyListResult?.data) {
                setHaveMore(false)
                setSearching(false)
                return
            }
            if (searchKeyword !== '') {
                // It is branch new search, so we need to clear old search result
                setSearchResult([companyListResult])
            } else {
                // User click on "View more"
                setSearchResult([...searchResult, companyListResult])
            }
            const isHaveMoreResult = filterPage + 1 < (companyListResult?.meta?.nbPages || 0)
            setHaveMore(isHaveMoreResult)
            setSearching(false)
        },
        [keyword, selectedIndustry, langCode, searchPage, searchResult, user]
    )

    const handleSearch = useCallback(
        async (keyword: string, category: number[]) => {
            const trimmedKeyword = keyword.trim()
            setSearch(trimmedKeyword !== '')
            setKeyword(trimmedKeyword)
            doSearch({
                searchKeyword: trimmedKeyword
            })
            updateHistory(trimmedKeyword, category)

            if (trimmedKeyword === '') {
                dispatch({
                    type: ACTION_SET_COMPANY_LIST,
                    payload: companyListProps
                })
            }
        },
        [doSearch]
    )

    const handleOnClickViewMore = () => {
        const nextPage = searchPage + 1
        setSearchPage(nextPage)
        doSearch({
            page: nextPage
        })
    }

    const handlePopstate = () => {
        const url = window.location.href
        const keywordOnPop = getParamFromUrl('keyword', url)
        // TODO: on popstate
        setSearch(keywordOnPop !== '')
        setKeyword(keywordOnPop)
        doSearch({
            searchKeyword: keywordOnPop
        })
        if (typeof window !== 'undefined') {
            // Scroll to top on popState
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }, 500)
        }
    }

    const updateUserState = (e: Event) => {
        if (!e?.detail?.user) {
            return
        }
        dispatch({
            type: ACTION_SET_USER,
            payload: e?.detail?.user
        })

        fetchCompanyList(e?.detail?.user)
    }

    const fetchCompanyList = async (user: TAuthUser) => {
        if (!user?.accessToken) {
            return
        }

        const rangesFilter = [
            {
                field: 'companyId',
                operator: '>',
                value: '0'
            }
        ]
        // lastPublishedOn desc
        const featuredCompanyList = await getCompanyList(
            {
                query: '',
                order: [
                    {
                        field: 'lastPostJobOn',
                        value: 'desc'
                    }
                ],
                hitsPerPage: LIMIT_COMPANY_LIST,
                filter: [
                    {
                        field: 'profilePublishedSiteMask',
                        value: '1,3'
                    }
                ],
                regexp: [
                    {
                        field: 'companySlug',
                        value: '.+'
                    }
                ],
                ranges: rangesFilter,
                userId: user?.userId,
                isJobs: true
            },
            langCode,
            user?.accessToken
        )

        dispatch({
            type: ACTION_SET_COMPANY_LIST,
            payload: [featuredCompanyList]
        })
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Scroll to top
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }, 500)
            window.addEventListener('popstate', handlePopstate)
            window.addEventListener(UPDATE_USER, updateUserState)
        }
        return () => {
            window.removeEventListener('popstate', handlePopstate)
            window.removeEventListener(UPDATE_USER, updateUserState)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (user === null) {
            return
        }
        dispatch({
            type: ACTION_SET_USER,
            payload: user
        })
    }, [dispatch, user])

    useEffect(() => {
        if (companyListProps.length === 0) {
            return
        }
        dispatch({
            type: ACTION_SET_COMPANY_LIST,
            payload: companyListProps
        })
    }, [dispatch, companyListProps])

    useEffect(() => {
        setKeyword(keywordFromUrl)
        if (keywordFromUrl !== '') {
            setSearch(true)
            doSearch({
                searchKeyword: keywordFromUrl,
                searchIndustries: industriesFromUrl
            })
            updateHistory(keywordFromUrl, industriesFromUrl)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordFromUrl, industriesFromUrl])

    return (
        <HogProvider
            posthogConfig={{
                key: process.env.NEXT_PUBLIC_POSTHOG_API_KEY ?? '',
                host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '',
                options: {
                    optOutUserAgentFilter: isOptOutUserAgentFilter
                }
            }}
        >
            <MasterLayout
                seoKeywords={seoKeywords}
                appMode={appMode}
                langCode={langCode}
                jsToken={jsToken}
                wsToken={wsToken}
                isMobileScale={isMobile}
                noPaddingResponsiveContainer
            >
                <Row id='topCompanyList'>
                    <Col>
                        <CompanySearchHeader
                            keywordFromUrl={keyword}
                            industriesFromUrl={selectedIndustry}
                            langCode={langCode}
                            handleSearch={handleSearch}
                            onChangeIndustries={(value: number[]) => {
                                setSelectedIndustry(value)
                                doSearch({
                                    searchKeyword: keyword,
                                    searchIndustries: value
                                })
                            }}
                        />
                    </Col>
                </Row>
                {isSearch ? (
                    <CompanySearchResult
                        t={t}
                        companyList={searchResult}
                        keyword={keyword}
                        setKeyword={setKeyword}
                        isSearching={isSearching}
                        isHaveMore={isHaveMore}
                        handleOnClickViewMore={handleOnClickViewMore}
                    />
                ) : (
                    <>
                        <Row>
                            <CompanyListSection title={t('Featured Companies')} langCode={langCode} />
                        </Row>
                    </>
                )}

                <GotoTop trackId='topCompanyList' />
            </MasterLayout>
        </HogProvider>
    )
}

export default CompanyListPage
